// useAITaskProgress.js
// WebSocket hook for real-time AI task progress updates

import { useState, useEffect, useCallback, useRef } from 'react';

const WS_RECONNECT_INTERVAL = 3000;
const WS_MAX_RETRIES = 5;

export const useAITaskProgress = (userId = 'default') => {
  const [connected, setConnected] = useState(false);
  const [taskProgress, setTaskProgress] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);

  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/ai-tasks/${userId}`;
  }, [userId]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[AI Task WS] Connected');
        setConnected(true);
        retriesRef.current = 0;
        
        // Subscribe to all task updates
        wsRef.current.send(JSON.stringify({ type: 'subscribe_all' }));
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'task_progress') {
            setTaskProgress(prev => ({
              ...prev,
              [data.task_id]: {
                progress: data.progress,
                stage: data.stage,
                details: data.details,
                timestamp: data.timestamp
              }
            }));
            setLastUpdate(data);
          } else if (data.type === 'subscribed' || data.type === 'subscribed_all') {
            console.log('[AI Task WS] Subscription confirmed:', data.message);
          }
        } catch (err) {
          console.error('[AI Task WS] Parse error:', err);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('[AI Task WS] Disconnected', event.code);
        setConnected(false);
        
        // Attempt reconnect
        if (retriesRef.current < WS_MAX_RETRIES) {
          retriesRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`[AI Task WS] Reconnecting... (attempt ${retriesRef.current})`);
            connect();
          }, WS_RECONNECT_INTERVAL);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[AI Task WS] Error:', error);
      };
    } catch (err) {
      console.error('[AI Task WS] Connection failed:', err);
    }
  }, [getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const subscribeToTask = useCallback((taskId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        task_id: taskId
      }));
    }
  }, []);

  const unsubscribeFromTask = useCallback((taskId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe',
        task_id: taskId
      }));
    }
    setTaskProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[taskId];
      return newProgress;
    });
  }, []);

  const getTaskProgress = useCallback((taskId) => {
    return taskProgress[taskId] || null;
  }, [taskProgress]);

  const clearProgress = useCallback((taskId) => {
    if (taskId) {
      setTaskProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[taskId];
        return newProgress;
      });
    } else {
      setTaskProgress({});
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connected,
    taskProgress,
    lastUpdate,
    subscribeToTask,
    unsubscribeFromTask,
    getTaskProgress,
    clearProgress,
    reconnect: connect
  };
};

export default useAITaskProgress;
